import { FC, useCallback, useEffect, useState } from "react";

import { FieldValues } from "react-hook-form";

import { GlobalLoader } from "../Loader";
import { PromptInfo, Step, SubmissionInfo } from "../types";
import {
  checkIsDocumentSuccessResponse,
  checkIsLastDocumentSuccessResponse,
  DocumentResponse
} from "../../hooks/DocumentSuccessResponse";
import { WrapperForDocumentForm } from "../DocumentForm/WrapperForDocumentForm";
import { SubmissionInformation } from "../SubmissionInformation/SubmissionInformation";
import { useDocumentFetchingInfrastructure } from "../../hooks/useDocumentFetchingInfrastructure";
import { RetryDocumentRetrieval } from "./RetryDocumentRetrieval";
//TODO: refactor name
export enum DocumentRetrievalState {
  Loading,
  Document,
  SubmissionInfo,
  Retry,
}

type LoopDocumentRetrieverProps = {
  contractId?: number | string;
  setStep?: (step: Step) => void;
  setPromptInfo?: (promptInfo: PromptInfo) => void;
};

export const LoopDocumentRetriever: FC<LoopDocumentRetrieverProps> = ({
  contractId,
  setStep,
  setPromptInfo,
}) => {
  const [documentResponse, setDocumentResponse] =
    useState<DocumentResponse | null>(null);
  const [documentRetrievalState, setDocumentRetrievalState] = useState(
    DocumentRetrievalState.Loading
  );
  const [submissionInfo, setSubmissionInfo] = useState<SubmissionInfo | null>(
    null
  );

  const displaySubmissionInfo = useCallback((info: SubmissionInfo | null) => {
    setSubmissionInfo(info);
    setDocumentRetrievalState(DocumentRetrievalState.SubmissionInfo);
  }, []);

  const { getDocument, postDocument } = useDocumentFetchingInfrastructure(
    setDocumentRetrievalState,
    setDocumentResponse,
    displaySubmissionInfo,
    contractId
  );

  useEffect(() => {
    getDocument();
  }, [getDocument]);

  const handleSubmitDocument = (formData: FieldValues) => {
    if (
      !documentResponse ||
      !checkIsDocumentSuccessResponse(documentResponse)
    ) {
      return;
    }
    setDocumentRetrievalState(DocumentRetrievalState.Loading);

    window.scrollTo(0, 0);
    postDocument(formData, documentResponse.id);
  };

  const isLastDocument =
    !!documentResponse && checkIsLastDocumentSuccessResponse(documentResponse);

  const nextVisit =
    documentResponse && checkIsLastDocumentSuccessResponse(documentResponse)
      ? documentResponse.nextVisit
      : undefined;

  const handleSubmissionInfoEnd = () => {
    if (!isLastDocument) {
      setDocumentRetrievalState(DocumentRetrievalState.Document);
      return;
    }

    // This visit is fully prepared. Offer the next one rather than dropping the
    // patient back on the login screen.
    if (nextVisit && setStep && setPromptInfo) {
      setPromptInfo({
        title: "One more visit to prepare",
        message: "This visit is ready. One more appointment still needs your paperwork.",
        errorMessage: "",
        redMessage: "",
        nextStep: Step.PHONE_AUTHORIZATION,
        nextVisit,
        okLabel: "Continue",
        secondaryLabel: "I'll do this later",
        reloginOnOk: true,
      });
      setStep(Step.PROMPT);
      return;
    }

    window.location.href = "/";
  };

  return (
    <>
      {documentRetrievalState === DocumentRetrievalState.Loading && (
        <GlobalLoader />
      )}

      {documentRetrievalState === DocumentRetrievalState.SubmissionInfo &&
        submissionInfo && (
          <SubmissionInformation
            info={submissionInfo}
            cbOnEndOfAnimation={handleSubmissionInfoEnd}
            displayTime={isLastDocument ? 3000 : 1300}
          />
        )}

      {documentResponse && checkIsDocumentSuccessResponse(documentResponse) && (
        <WrapperForDocumentForm
          documentResponse={documentResponse}
          onFormDataSubmit={handleSubmitDocument}
          visible={documentRetrievalState === DocumentRetrievalState.Document}
        />
      )}

      {documentRetrievalState === DocumentRetrievalState.Retry && (
        <RetryDocumentRetrieval
          onRetryClick={async () => {
            await getDocument();
          }}
        />
      )}
    </>
  );
};
